import { useQuery } from "@tanstack/react-query";
import type { EditorSelection } from "forest/monaco-editor/MonacoEditor";
import { Check, ChevronRight, Copy, FileCode, X } from "lucide-react";
import { useEffect, useState } from "react";

import type { SerializedNode } from "@/bear-sandbox/query";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  deselectNodes,
  highlightNodes,
  selectNodes,
  subscribeSelectionChange,
  unhighlightNodes,
} from "@/lib/bear-sandbox-bridge";

import {
  type ConnectionRow,
  connectionRows,
  dimensionRows,
  type InspectorRow,
  nodeLabel,
  propRows,
  sourceJumpTarget,
  specificationRows,
} from "./inspector-utils";
import { selectedNodesQuery } from "./selected-nodes-query";
import { ShapeThumbnail } from "./shape-thumbnail";

/** Copies node ids so they can be pasted into the chat. */
const CopyButton = ({ text, label }: { text: string; label: string }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 shrink-0"
          aria-label={label}
          onClick={async () => {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
};

interface JumpProps {
  fileExists: (path: string) => boolean;
  onOpenPath: (path: string, selection?: EditorSelection) => void;
}

/** Opens the editor at the JSX call site that produced the node. */
const JumpToSourceButton = ({
  node,
  fileExists,
  onOpenPath,
}: { node: SerializedNode } & JumpProps) => {
  const target = sourceJumpTarget(node.source, fileExists);
  if (!target) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 shrink-0"
          aria-label="Jump to source"
          onClick={() => onOpenPath(target.path, target.selection)}
        >
          <FileCode className="h-3.5 w-3.5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Jump to source</TooltipContent>
    </Tooltip>
  );
};

const RowList = ({ rows }: { rows: InspectorRow[] }) => (
  <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
    {rows.map((row, index) => (
      <div key={`${row.key}-${index}`} className="contents">
        <dt className="truncate text-muted-foreground">{row.key}</dt>
        {typeof row.value === "string" ? (
          <dd className="truncate font-mono" title={row.value}>
            {row.value}
          </dd>
        ) : (
          <dd>
            <ShapeThumbnail points={row.value} />
          </dd>
        )}
      </div>
    ))}
  </dl>
);

/**
 * What the node is attached to. Each endpoint is a button: clicking it makes
 * that node the selection, which is how you walk from a hanger to the joist
 * it seats.
 */
const ConnectionList = ({ rows }: { rows: ConnectionRow[] }) => (
  <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
    {rows.map((row) => (
      <div key={`${row.key}-${row.fqn}`} className="contents">
        <dt className="truncate text-muted-foreground">{row.key}</dt>
        <dd className="min-w-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="block max-w-full truncate font-mono underline-offset-2 hover:underline"
                onMouseEnter={() => highlightNodes([row.fqn])}
                onMouseLeave={() => unhighlightNodes([row.fqn])}
                onClick={() => selectNodes([row.fqn])}
              >
                {row.label}
              </button>
            </TooltipTrigger>
            <TooltipContent className="font-mono">{row.fqn}</TooltipContent>
          </Tooltip>
          {row.detail.length > 0 ? (
            <div className="text-muted-foreground">
              <RowList rows={row.detail} />
            </div>
          ) : null}
        </dd>
      </div>
    ))}
  </dl>
);

const NodeDetails = ({ node }: { node: SerializedNode }) => {
  const specification = specificationRows(node);
  const summary = [...specification, ...dimensionRows(node)];
  const connections = connectionRows(node);
  return (
    <div className="flex flex-col gap-2 px-3 py-2">
      {summary.length > 0 ? <RowList rows={summary} /> : null}
      {connections.length > 0 ? (
        <div className="flex flex-col gap-1 border-t border-border/60 pt-2">
          <span className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
            Connections
          </span>
          <ConnectionList rows={connections} />
        </div>
      ) : null}
      <Collapsible
        defaultOpen={specification.length === 0}
        className="border-t border-border/60 pt-2"
      >
        <CollapsibleTrigger className="group flex w-full items-center gap-1 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
          <ChevronRight className="h-3 w-3 transition-transform group-data-[state=open]:rotate-90" />
          All props
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-1">
          <RowList rows={propRows(node)} />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

const NodeSection = ({
  node,
  ...jump
}: { node: SerializedNode } & JumpProps) => (
  <Collapsible
    className="border-t border-border/60 first:border-t-0"
    onMouseEnter={() => highlightNodes([node.fqn])}
    onMouseLeave={() => unhighlightNodes([node.fqn])}
  >
    <div className="flex items-center gap-1 pr-1 hover:bg-muted/60">
      <Tooltip>
        <TooltipTrigger asChild>
          <CollapsibleTrigger className="group flex min-w-0 flex-1 items-center gap-1.5 px-2 py-1.5 text-left text-xs">
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-90" />
            <span className="truncate font-mono">{nodeLabel(node)}</span>
          </CollapsibleTrigger>
        </TooltipTrigger>
        <TooltipContent className="font-mono">{node.fqn}</TooltipContent>
      </Tooltip>
      <CopyButton text={node.fqn} label="Copy node id" />
      <JumpToSourceButton node={node} {...jump} />
    </div>
    <CollapsibleContent>
      <NodeDetails node={node} />
    </CollapsibleContent>
  </Collapsible>
);

/**
 * Floating inspector for the current 3D selection: node id plus properties,
 * one disclosure per node when several are selected.
 */
const BearInspectorOverlay = ({ fileExists, onOpenPath }: JumpProps) => {
  const [nodeIds, setNodeIds] = useState<string[]>([]);

  useEffect(() => subscribeSelectionChange(setNodeIds), []);

  const { data: nodes = [] } = useQuery(selectedNodesQuery(nodeIds));

  if (nodes.length === 0) return null;
  const single = nodes.length === 1 ? nodes[0] : null;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="absolute top-3 left-3 flex max-h-[calc(100%-1.5rem)] w-72 flex-col overflow-hidden rounded-lg border border-border bg-background/95 shadow-lg backdrop-blur">
        <div className="flex items-center gap-1 border-b border-border px-3 py-2">
          {single ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="min-w-0 flex-1 truncate font-mono text-xs">
                  {single.fqn}
                </span>
              </TooltipTrigger>
              <TooltipContent className="font-mono">
                {single.fqn}
              </TooltipContent>
            </Tooltip>
          ) : (
            <span className="min-w-0 flex-1 truncate text-xs">
              {nodes.length} selected
            </span>
          )}
          <CopyButton
            text={nodes.map((node) => node.fqn).join("\n")}
            label={single ? "Copy node id" : "Copy every node id"}
          />
          {single ? (
            <JumpToSourceButton
              node={single}
              fileExists={fileExists}
              onOpenPath={onOpenPath}
            />
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={() => deselectNodes()}
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="min-h-0 overflow-y-auto">
          {single ? (
            <NodeDetails node={single} />
          ) : (
            nodes.map((node) => (
              <NodeSection
                key={node.fqn}
                node={node}
                fileExists={fileExists}
                onOpenPath={onOpenPath}
              />
            ))
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default BearInspectorOverlay;
